import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, btnPrimary } from '../styles/theme.js'
import { join as joinApi } from '../lib/api.js'
import { authModel } from '../models/auth.js'

const container = css`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 70vh;
`

const joinCard = css`
  ${card};
  width: 100%;
  max-width: 420px;
  text-align: center;
`

const heading = css`
  font-size: ${font.size['2xl']};
  font-weight: ${font.weight.bold};
  margin-bottom: ${space.sm};
  color: ${colors.text};
`

const subtext = css`
  font-size: ${font.size.sm};
  color: ${colors.textSecondary};
  margin-bottom: ${space.lg};
`

const roleBadge = css`
  display: inline-block;
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${colors.teal};
  background: ${colors.teal}15;
`

const errorStyle = css`
  font-size: ${font.size.sm};
  color: ${colors.critical};
  margin-bottom: ${space.md};
`

const successStyle = css`
  font-size: ${font.size.sm};
  color: ${colors.teal};
  margin-bottom: ${space.md};
`

export function JoinView() {
  let state = 'loading' // loading | valid | joining | joined | error
  let inviteInfo = null
  let errorMsg = ''
  let alreadyMember = false

  async function loadInvite(code) {
    state = 'loading'
    try {
      inviteInfo = await joinApi.validate(code)

      if (!authModel.isLoggedIn) {
        m.route.set(`/login?project_invite=${code}`)
        return
      }

      state = 'valid'
    } catch (err) {
      state = 'error'
      if (err.status === 410) {
        errorMsg = 'This invite link has been revoked.'
      } else if (err.status === 404) {
        errorMsg = 'This invite link is not valid.'
      } else {
        errorMsg = err.message || 'Failed to load invite'
      }
    }
    m.redraw()
  }

  async function acceptInvite(code) {
    state = 'joining'
    m.redraw()
    try {
      const result = await joinApi.accept(code)
      alreadyMember = result.alreadyMember
      state = 'joined'
      m.redraw()
      setTimeout(() => {
        m.route.set(`/projects/${result.projectId}`)
      }, 1200)
    } catch (err) {
      state = 'error'
      errorMsg = err.message || 'Failed to join project'
      m.redraw()
    }
  }

  return {
    oninit(vnode) {
      loadInvite(vnode.attrs.code)
    },

    view(vnode) {
      const code = vnode.attrs.code

      if (state === 'loading') {
        return m('div', { class: container }, m('div', 'Loading...'))
      }

      if (state === 'error') {
        return m('div', { class: container },
          m('div', { class: joinCard }, [
            m('h2', { class: heading }, 'Unable to join'),
            m('p', { class: errorStyle }, errorMsg),
          ])
        )
      }

      if (state === 'joined') {
        const msg = alreadyMember
          ? `You're already a member of ${inviteInfo.projectName}. Redirecting...`
          : `Joined ${inviteInfo.projectName}! Redirecting...`
        return m('div', { class: container },
          m('div', { class: joinCard }, [
            m('h2', { class: heading }, alreadyMember ? 'Already a member' : 'Joined!'),
            m('p', { class: successStyle }, msg),
          ])
        )
      }

      // valid or joining
      return m('div', { class: container },
        m('div', { class: joinCard }, [
          m('h2', { class: heading }, `Join ${inviteInfo.projectName}`),
          m('p', { class: subtext }, [
            'You\'ve been invited to join as ',
            m('span', { class: roleBadge }, inviteInfo.role),
          ]),
          m('button', {
            class: `${btnPrimary} ${css`width: 100%; justify-content: center;`}`,
            disabled: state === 'joining',
            onclick: () => acceptInvite(code),
          }, state === 'joining' ? 'Joining...' : 'Join project'),
        ])
      )
    },
  }
}
